import { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TimeEntryCategory, TimeEntryCategorySchema, TimeEntryConflictItem } from "@myapp/contract";
import PageLayout from "../../../shared/ui/layouts/PageLayout/PageLayout";
import { routes } from "../../../shared/config/routes";
import { useCreateTimeManagement, getTimeEntryCategoryLabel } from "../../../entities/time-entry";
import {
  TimeManagementFormWidget,
  toCreateDto,
  type TimeManagementFormValues,
} from "../../../features/time-management";
import { useSuccessDialog } from "../../../shared/hooks/useSuccessDialog";
import { SuccessDialog } from "../../../shared/ui/feedback/SuccessDialog/SuccessDialog";
import { ConflictDialog } from "../../../shared/ui/feedback/ConflictDialog/ConflictDialog";
import { BlockerDialog } from "../../../shared/ui/feedback/BlockerDialog/BlockerDialog";
import { parseConflictsFromError } from "../../../shared/lib/errors/parseConflictsFromError";
import { parseCompletedBlockersFromError } from "../../../shared/lib/errors/parseCompletedBlockersFromError";

export function TimeManagementCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const createMutation = useCreateTimeManagement();
  const submitRef = useRef<(() => void) | null>(null);
  const { showSuccess, dialogProps } = useSuccessDialog();

  const [conflicts, setConflicts] = useState<TimeEntryConflictItem[] | null>(null);
  const [blockers, setBlockers] = useState<TimeEntryConflictItem[] | null>(null);
  const pendingValuesRef = useRef<TimeManagementFormValues | null>(null);

  const categoryParam = searchParams.get("category");
  const parsed = TimeEntryCategorySchema.safeParse(categoryParam);

  if (!parsed.success) {
    navigate(routes.zeitmanagement.root, { replace: true });
    return null;
  }

  const category: TimeEntryCategory = parsed.data;

  const handleSubmit = async (values: TimeManagementFormValues, force = false) => {
    try {
      const dto = toCreateDto(values, force);
      await createMutation.mutateAsync(dto);
      showSuccess("Erfolgreich gespeichert", () => navigate(routes.zeitmanagement.root));
    } catch (err) {
      const blocking = parseCompletedBlockersFromError(err);
      if (blocking) {
        setBlockers(blocking);
        return;
      }
      const detected = parseConflictsFromError(err);
      if (detected) {
        pendingValuesRef.current = values;
        setConflicts(detected);
      } else {
        throw err;
      }
    }
  };

  const handleConflictConfirm = async () => {
    if (!pendingValuesRef.current) return;
    await handleSubmit(pendingValuesRef.current, true);
    setConflicts(null);
    pendingValuesRef.current = null;
  };

  const handleConflictCancel = () => {
    setConflicts(null);
    pendingValuesRef.current = null;
  };

  return (
    <>
      <PageLayout
        pageTitle={`${getTimeEntryCategoryLabel(category)} planen`}
        scrollable
        back
        backTo={routes.zeitmanagement.root}
        primaryAction={{
          label: "Speichern",
          onClick: () => submitRef.current?.(),
          loading: createMutation.isPending,
        }}
      >
        <TimeManagementFormWidget
          category={category}
          onSubmit={handleSubmit}
          isLoading={createMutation.isPending}
          submitRef={submitRef}
        />
      </PageLayout>
      <SuccessDialog
        open={dialogProps.open}
        onOpenChange={(open) => { if (!open) dialogProps.onClose(); }}
        title={dialogProps.title}
        onClose={dialogProps.onClose}
      />
      <ConflictDialog
        open={conflicts !== null}
        conflicts={conflicts ?? []}
        onConfirm={handleConflictConfirm}
        onCancel={handleConflictCancel}
        isLoading={createMutation.isPending}
      />
      <BlockerDialog
        open={blockers !== null}
        blockers={blockers ?? []}
        onClose={() => setBlockers(null)}
        onGoToReschedule={() => navigate(routes.reschedule.root)}
      />
    </>
  );
}
